import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/24px';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { HStack } from 'design-system/components/Stack';
import { Heading } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';

export const ModalContainer = styled.div<{ size?: 'md' | 'lg' | 'xl' }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: auto;
  max-height: 100%;
  width: ${({ theme, size }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.modal.width[size];
  }};
  max-width: 100vw;
  background-color: ${({ theme }) => theme.color.primary.white};
  border-radius: ${({ theme, size }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.modal.borderRadius[size];
  }};
  padding-top: ${Spacing[7]};
`;

ModalContainer.defaultProps = {
  size: 'md',
};

export const ModalBody = styled.section`
  flex: 1 1 auto;
  overflow-y: auto;
  margin: 0;
  padding: ${Spacing[7]};
  padding-top: ${BASE * 2.5}px;
`;

export const CloseContainer = styled.div.attrs(() => ({ role: 'button' }))`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 ${Spacing[2]};
  height: ${({ theme }) => theme.text.heading.md.lineHeight};

  svg {
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  &:hover svg {
    color: ${({ theme }) => theme.color.text.disabled};
  }
`;

const Header = styled.header`
  flex: none;
  margin: 0;
  padding: 0 ${Spacing[7]};

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
`;

const PrefixIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${Spacing[1]};
  margin-right: ${Spacing[2]};
  cursor: pointer;
`;

const Description = styled.div`
  flex: none;
  padding: ${Spacing[2]} ${Spacing[7]} 0 ${Spacing[7]};
`;

interface ModalHeaderProps {
  className?: string;
  children: ReactNode;
  prefixIcon?: ReactNode;
  onPrefixIconClick?: () => void;
  // requiring undefined here increases the friction of explicitly not
  // providing onClose, which is valid but should be uncommon.
  onClose?: () => void | undefined;
  'data-test'?: string;
}

export const ModalHeader = ({
  className,
  onPrefixIconClick,
  onClose,
  children,
  prefixIcon,
  'data-test': dataTest,
}: ModalHeaderProps) => (
  <Header className={className} data-test={dataTest}>
    <HeaderLeft>
      {prefixIcon && (
        <PrefixIconContainer aria-label='Go back' onClick={onPrefixIconClick}>
          {prefixIcon}
        </PrefixIconContainer>
      )}
      <Heading size='md'>{children}</Heading>
    </HeaderLeft>
    {onClose != null && (
      <CloseContainer
        aria-label='Close this modal'
        id='modal__close'
        onClick={onClose}
        data-test='modal__close'
      >
        <XIcon />
      </CloseContainer>
    )}
  </Header>
);

interface ModalDescriptionProps {
  children: ReactNode;
}

export const ModalDescription = ({ children }: ModalDescriptionProps) => (
  <Description>{children}</Description>
);

const Footer = styled.footer`
  flex: none;
  padding: ${Spacing[7]};
  padding-top: 0;
`;

interface ModalFooterProps {
  className?: string;
  children: ReactNode;
  'data-test'?: string;
}

export const ModalFooter = ({ className, children, 'data-test': dataTest }: ModalFooterProps) => (
  <Footer className={className} data-test={dataTest}>
    <HStack>{children}</HStack>
  </Footer>
);
